/**
 * AI script helpers — draft from a brief, improve/tighten, and segment & cue.
 * Every result is a SUGGESTION: it lands in a preview and is only applied to the
 * script when the producer/admin clicks Apply. The key stays server-side.
 */
import { useState } from 'react';
import { parseScript, serializeScript } from '@goanna-im/shared';
import type { Project } from '@goanna-im/shared';
import { generateScript } from '../lib/ai.js';
import type { ScriptAiMode } from '../lib/ai.js';

const MODES: { id: ScriptAiMode; label: string; hint: string }[] = [
  { id: 'draft', label: 'Draft from a brief', hint: 'Generate a first-pass script from a topic or bullet points.' },
  { id: 'improve', label: 'Improve / tighten', hint: 'Rewrite the current script for clarity, tone, or a target read-time.' },
  { id: 'segment', label: 'Segment & cues', hint: 'Add section breaks, pauses/camera cues and hot-facts to the current script.' },
];

export function AiScriptPanel({
  currentProject,
  onApply,
  onError,
}: {
  currentProject: Project;
  onApply: (p: Project) => void;
  onError?: (msg: string) => void;
}) {
  const [mode, setMode] = useState<ScriptAiMode>('improve');
  const [brief, setBrief] = useState('');
  const [seconds, setSeconds] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ text: string; sample: boolean; provider: string } | null>(null);
  const [status, setStatus] = useState('');

  const report = (m: string) => {
    setStatus(m);
    onError?.(m);
  };

  const run = async () => {
    setBusy(true);
    setStatus('');
    setResult(null);
    try {
      const res = await generateScript({
        mode,
        brief: mode === 'draft' ? brief : undefined,
        script: mode !== 'draft' ? serializeScript(currentProject) : undefined,
        targetSeconds: seconds ? Number(seconds) : undefined,
      });
      setResult({ text: res.text, sample: res.sample, provider: res.provider });
    } catch (e) {
      report(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const apply = () => {
    if (!result) return;
    const title = mode === 'draft' ? 'AI draft' : currentProject.title || 'Script';
    const parsed = parseScript(result.text, title);
    // Improve/segment keep editing the same script — preserve its identity so the
    // cloud library record and scroll position stay linked. A draft is a new script.
    const applied =
      mode === 'draft'
        ? parsed
        : { ...parsed, id: currentProject.id, createdAt: currentProject.createdAt };
    onApply(applied);
    // Keep the suggestion on screen so it can be re-applied or referenced.
    setStatus('Applied to the script ✓');
  };

  return (
    <section className="card stack">
      <h3 style={{ margin: 0 }}>✨ AI script help</h3>
      <div className="row" style={{ gap: '0.3rem', flexWrap: 'wrap' }}>
        {MODES.map((m) => (
          <button
            key={m.id}
            className={mode === m.id ? 'primary' : ''}
            onClick={() => {
              setMode(m.id);
              setResult(null);
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>
        {MODES.find((m) => m.id === mode)?.hint}
      </p>

      {mode === 'draft' && (
        <textarea
          placeholder="What's the script about? A topic, or a few bullet points…"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          style={{ minHeight: 80 }}
        />
      )}
      {(mode === 'draft' || mode === 'improve') && (
        <label className="row" style={{ gap: '0.4rem', fontSize: '0.85rem' }}>
          <span className="muted">Target read-time (seconds, optional)</span>
          <input
            type="number"
            min="0"
            value={seconds}
            onChange={(e) => setSeconds(e.target.value)}
            style={{ width: 90 }}
            placeholder="e.g. 60"
          />
        </label>
      )}

      <button className="primary" onClick={run} disabled={busy || (mode === 'draft' && !brief.trim())}>
        {busy ? 'Generating…' : 'Generate'}
      </button>
      {status && <span className="tag readout">{status}</span>}

      {result && (
        <div className="stack" style={{ borderTop: '1px dashed var(--border, #334)', paddingTop: '0.5rem' }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong style={{ fontSize: '0.85rem' }}>
              Suggestion{result.sample ? ' (sample — AI not configured yet)' : ''}
            </strong>
            <span className="muted" style={{ fontSize: '0.75rem' }}>{result.provider}</span>
          </div>
          <textarea
            readOnly
            value={result.text}
            className="script-editor"
            style={{ minHeight: 200 }}
          />
          <div className="row" style={{ gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={() => setResult(null)}>Discard</button>
            <button className="primary" onClick={apply} disabled={result.sample}>
              Apply to script
            </button>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: '0.75rem' }}>
            Review before applying — AI output is a suggestion, not verified fact.
          </p>
        </div>
      )}
    </section>
  );
}
